import { HttpClient, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { getTenantId } from 'src/app/services/store-snapshot.service';
import { environment } from 'src/environments/environment';
import { FormDataService } from '../../shared/attachments/service/form-data.service';
import {
  IDocumentWorkingCopy,
  IDocumentWorkingCopyRequest,
  IDocumentWorkingCopyUpdateRequest,
} from '../models/document-working-copy.model';

@Injectable({
  providedIn: 'root',
})
export class DocumentWorkingCopyApiService {
  constructor(private http: HttpClient) {}

  getLatestWorkingCopy(): Observable<IDocumentWorkingCopy | undefined> {
    return this.readWorkingCopy(
      this.http.get<IDocumentWorkingCopy | null>(`${this.urlFor()}/latest`, {
        observe: 'response',
      })
    );
  }

  saveWorkingCopy(
    workingCopy: IDocumentWorkingCopyRequest,
    tenantId?: string
  ): Observable<IDocumentWorkingCopy> {
    const formData = FormDataService.mapToFormData(workingCopy);
    return this.http.post<IDocumentWorkingCopy>(this.urlFor(tenantId), formData);
  }

  updateWorkingCopy(
    workingCopy: IDocumentWorkingCopyUpdateRequest,
    tenantId?: string
  ): Observable<IDocumentWorkingCopy> {
    const formData = FormDataService.mapToFormData(workingCopy);
    return this.http.put<IDocumentWorkingCopy>(this.urlFor(tenantId), formData);
  }

  deleteWorkingCopy(tenantId?: string): Observable<void> {
    return this.http.delete<void>(this.urlFor(tenantId));
  }

  private urlFor(tenantId?: string): string {
    return `${environment.apiUrl}/api/${tenantId || getTenantId()}/document-working-copies`;
  }

  private readWorkingCopy(
    request: Observable<HttpResponse<IDocumentWorkingCopy | IDocumentWorkingCopy[] | null>>
  ): Observable<IDocumentWorkingCopy | undefined> {
    return request.pipe(
      map((response) => {
        if (response.status === HttpStatusCode.NoContent || !response.body) {
          return undefined;
        }

        return this.normalizeResponse(response.body);
      }),
      catchError(() => of(undefined))
    );
  }

  private normalizeResponse(
    response: IDocumentWorkingCopy | IDocumentWorkingCopy[] | null
  ): IDocumentWorkingCopy | undefined {
    if (!response) {
      return undefined;
    }

    if (Array.isArray(response)) {
      return response[0];
    }

    return response;
  }
}
